import { useMemo, useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import AccountSelect from '../../components/AccountSelect';
import {
  reportsApi,
  DEBT_STATEMENT_ITEM_TYPE,
  type DebtStatementDto,
  type DebtStatementPersonSummaryDto,
} from '../../api/reports';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';
import { formatMoney } from '../../utils/formatMoney';

/**
 * SKOLŲ SUDERINIMO AKTAS — legacy R_DebtStatementItemList: per-person debt
 * confirmation act on one debt account. Two steps: pick the person (persons
 * with non-zero balance/turnover in the window), then render the act. The act
 * prints via the browser (print:hidden toolbars; the act block is the page).
 */
export default function DebtStatement() {
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [account, setAccount] = useState<number | null>(null);
  const [persons, setPersons] = useState<DebtStatementPersonSummaryDto[] | null>(null);
  const [act, setAct] = useState<DebtStatementDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPersons = async () => {
    if (!account) {
      setError('Pasirinkite skolos sąskaitą.');
      return;
    }
    setLoading(true);
    setError('');
    setPersons(null);
    setAct(null);
    try {
      setPersons(await reportsApi.debtStatementPersons({ from, to, account }));
    } catch (e: any) {
      setError(e.message || 'Nepavyko gauti asmenų sąrašo');
    } finally {
      setLoading(false);
    }
  };

  const loadAct = async (personId: number) => {
    if (!account) return;
    setLoading(true);
    setError('');
    try {
      setAct(await reportsApi.debtStatement({ from, to, account, personId }));
    } catch (e: any) {
      setError(e.message || 'Nepavyko sugeneruoti akto');
    } finally {
      setLoading(false);
    }
  };

  const handleCsv = async () => {
    if (!account || !act) return;
    try {
      await reportsApi.debtStatementCsv({ from, to, account, personId: act.personId });
    } catch (e: any) {
      setError(e.message || 'Eksportas nepavyko');
    }
  };

  const recordCount = act?.rows.filter(r => r.itemType === DEBT_STATEMENT_ITEM_TYPE.Transaction).length ?? 0;
  const screenPreview = useMemo(
    () =>
      (persons ?? []).slice(0, 10).map(p => `${p.personName}: likutis ${formatMoney(p.balanceEnd)}`.substring(0, 120)),
    [persons],
  );

  useReportScreen(
    act
      ? `Skolų suderinimo aktas Nr. ${act.statementNumber} (${act.personName}, ${act.from}–${act.to}): ${recordCount} operacijų`
      : 'Skolų suderinimo aktas: ne sugeneruotas',
    {
      recordCount: persons?.length ?? 0,
      filters: { nuo: from, iki: to, ...(account != null && { saskaita: String(account) }) },
      preview: screenPreview,
    },
  );

  const itemLabel = (itemType: number) =>
    itemType === DEBT_STATEMENT_ITEM_TYPE.BalanceStart
      ? 'Likutis laikotarpio pradžiai:'
      : itemType === DEBT_STATEMENT_ITEM_TYPE.Turnover
        ? 'Apyvarta per laikotarpį:'
        : itemType === DEBT_STATEMENT_ITEM_TYPE.BalanceEnd
          ? 'Likutis laikotarpio pabaigai:'
          : null;

  return (
    <div className="space-y-4">
      <ReportsNav />
      <h1 className="print:hidden">Skolų suderinimo aktas</h1>

      <div
        className="filters print:hidden"
        style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}
      >
        <label>
          Nuo: <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label>
          Iki: <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <label>
          Skolos sąskaita: <AccountSelect value={account} onChange={setAccount} />
        </label>
        <button onClick={loadPersons} disabled={loading || !account}>
          {loading ? <LoadingSpinner /> : 'Asmenys'}
        </button>
      </div>

      {error && <div className="error-message print:hidden">{error}</div>}

      {/* Step 1 — person picker (legacy picker list semantics). */}
      {persons && !act && (
        <div className="card print:hidden">
          <h2>Asmenys ({persons.length})</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Asmuo</th>
                <th>Kodas</th>
                <th style={{ textAlign: 'right' }}>Likutis pabaigai</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {persons.map(p => (
                <tr key={p.personId}>
                  <td>{p.personName}</td>
                  <td>{p.personCode ?? ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    <MoneyCell value={p.balanceEnd} />
                  </td>
                  <td>
                    <button onClick={() => loadAct(p.personId)}>Aktas</button>
                  </td>
                </tr>
              ))}
              {persons.length === 0 && (
                <tr>
                  <td colSpan={4}>Periode šios sąskaitos asmenų veiklos nėra</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Step 2 — the act. Print CSS: this block alone is the printed page. */}
      {act && (
        <>
          <div
            className="print:hidden"
            style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
          >
            <button className="secondary" onClick={() => setAct(null)}>
              ← Asmenys
            </button>
            <button onClick={() => window.print()}>Spausdinti</button>
            <button className="secondary" onClick={handleCsv}>
              CSV
            </button>
          </div>

          <div
            className="card"
            style={{ maxWidth: '800px', padding: '2rem', background: '#fff' }}
          >
            <div style={{ textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>
              Aktas Nr. {act.statementNumber}
            </div>
            <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              SKOLŲ SUDERINIMO AKTAS
            </h2>
            <p>
              Sudarytas {new Date().toISOString().slice(0, 10)} laikotarpiui {act.from} – {act.to}{' '}
              (sąskaita {act.account}).
            </p>

            {/* GAVĖJAS block — the recipient the act confirms. */}
            <div style={{ margin: '1rem 0' }}>
              <strong>GAVĖJAS:</strong> {act.personName}
              {act.personCode ? `, kodas ${act.personCode}` : ''}
              {act.personAddress ? `, ${act.personAddress}` : ''}
            </div>

            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Dok. Nr.</th>
                  <th>Aprašymas</th>
                  <th style={{ textAlign: 'right' }}>Debetas</th>
                  <th style={{ textAlign: 'right' }}>Kreditas</th>
                </tr>
              </thead>
              <tbody>
                {act.rows.map((r, i) => {
                  const label = itemLabel(r.itemType);
                  return (
                    <tr key={i} style={label ? { fontWeight: 600 } : undefined}>
                      <td>{r.date ?? ''}</td>
                      <td>{r.documentNumber ?? ''}</td>
                      <td>{r.content}</td>
                      <td style={{ textAlign: 'right' }}>
                        {r.debit !== 0 ? <MoneyCell value={r.debit} /> : ''}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {r.credit !== 0 ? <MoneyCell value={r.credit} /> : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* The confirmation + exceptions clause — legacy act wording. */}
            <p style={{ marginTop: '1.5rem' }}>
              Prašome patvirtinti, kad jūsų skola mūsų apskaitoje nurodyta teisingai.
            </p>
            <p>Jūsų nurodyti skolos duomenys yra teisingi su tokiomis išimtimis:</p>
            <div style={{ height: '5rem', borderBottom: '1px solid #999', marginBottom: '1.5rem' }} />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '3rem',
                marginTop: '2rem',
              }}
            >
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ borderTop: '1px solid #333', paddingTop: '0.5rem' }}>
                  Buhalteris (parašas)
                </div>
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ borderTop: '1px solid #333', paddingTop: '0.5rem' }}>
                  {act.personName} (parašas)
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
